import { serviceTypeBadge } from '@/components/features/offerings/common';
import { LoaderElement } from '@/components/features/shared/LoaderElement';
import { NavHeader } from '@/components/features/shared/NavHeader';
import { UnexpectedError } from '@/components/features/shared/UnexpectedError';
import { PriceLabelWithTaxResponsive, getSubscriptionLabel, renderSinglePeriod } from '@/components/features/shared/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Agreement } from '@/domain/agreements/agreement';
import { CollectorStatus } from '@/domain/collectors/collector';
import { BillingType } from '@/domain/invoices/invoice';
import { BillingFrequency, ServiceType } from '@/domain/offerings/offering';
import { AgreementPayment, PaymentMethod } from '@/domain/payments/payments';
import { useAgreement } from '@/hooks/api/useAgreements';
import { useCollectors } from '@/hooks/api/useCollectors';
import { useOffering } from '@/hooks/api/useOfferings';
import { useParcelByContract } from '@/hooks/api/useParcels';
import { useAgreementCustomer, useSubscriptionPayment } from '@/hooks/api/usePayments';
import { convertToDigitStr } from '@/utils/helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { CustomerData } from './CustomerData';
import { ParcelContractData } from './ParcelContractData';
import { PaymentAccreditationForm, paymentFormSchema, type PaymentFormValues } from './PaymentAccreditationForm';
import { SubtotalsData } from './SubtotalsData';
import { useOnSubmit } from './useOnSubmit';
import { PageContainer } from '@/components/features/shared/PageContainer';

export const SubscriptionSinglePayment = ({ paymentId, billingId }: { paymentId: string, billingId: string }) => {
  const { data: agreementPayments, isLoading: isLoadingPayment, error: errorPayment } = useSubscriptionPayment(billingId, paymentId);
  const subContractId = agreementPayments?.[0]?.contractId;

  const { data: agreement, isLoading: isLoadingSubContract } = useAgreement(subContractId || '');
  const { data: mainContract, isLoading: isLoadingMainContract } = useAgreement(agreement?.mainContractId || '');

  // console.log(agreementPayments)

  if (isLoadingPayment || isLoadingSubContract || isLoadingMainContract)
    return <LoaderElement />

  if (errorPayment || !agreementPayments || !agreement)
    return <UnexpectedError />

  return <SubscriptionPayment paymentId={paymentId} agreementPayments={agreementPayments} subContract={agreement} mainContract={mainContract ?? agreement} />;
}

const SubscriptionPayment = ({ paymentId, agreementPayments, subContract, mainContract }
  : { paymentId: string, agreementPayments: AgreementPayment[], subContract: Agreement, mainContract: Agreement }) => {
  const { data: offering, isLoading: isLoadingOffering, error: errorOffering } = useOffering(subContract.offeringId || '')
  const { data: customer, isLoading: isLoadingCustomer } = useAgreementCustomer(mainContract?.id || mainContract?.mainContractId || subContract.id);
  const { data: collectors = [], isLoading: isLoadingCollectors } = useCollectors({ status: CollectorStatus.Active });

  const { data: parcel, isLoading: isLoadingParcel } = useParcelByContract({
    contractId: mainContract?.id!, enabled: offering?.serviceType === ServiceType.PARCELAS
  });

  const isLoading = isLoadingOffering || isLoadingCustomer || isLoadingCollectors;
  const error = errorOffering;

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      billingType: BillingType.Invoice,
      paymentMethod: PaymentMethod.BANK_TRANSFER,
      paymentDetails: '',
      paymentVerified: false,
      partialPayment: false,
      amount: undefined,
      interestAmount: undefined,
      collectorId: mainContract?.collectorId || null,
    },
  });

  const billingType = form.watch('billingType', BillingType.Invoice)
  const selectedPaymentMethod = form.watch('paymentMethod');
  const isPartialPayment = form.watch('partialPayment');
  const amount = form.watch('amount');
  const interestAmount = form.watch('interestAmount');
  const selectedCollectorId = form.watch('collectorId');

  const hasContractCollector = !!mainContract?.collectorId
  const selectedDifferentCollector = !!(hasContractCollector && selectedCollectorId && selectedCollectorId !== mainContract.collectorId);
  const assignedCollectorName = hasContractCollector && mainContract.collectorId ? collectors.find(c => c.id === mainContract.collectorId)?.name : null

  // const contractAmount = agreementPayments[0].contractAmount || 0;
  // const totalAmountToBePayed = agreementPayments?.[0]?.totalAmount || 0
  const totalAmountToBePayed = agreementPayments.reduce((total, payment) => total + payment.totalAmount, 0)

  const cardInterest = selectedPaymentMethod === PaymentMethod.CREDIT_CARD ? (interestAmount || 0) : 0;
  const amountToCharge = ((isPartialPayment ? amount : totalAmountToBePayed) || 0) + cardInterest;

  const amountOwed = 0
  // const amountOwed = Math.abs(totalAmountToBePayed - contractAmount);
  const amountToInvoice = (isPartialPayment ? (amount || 0) : (billingType === BillingType.Invoice
    && amountOwed > 0 ? (totalAmountToBePayed + cardInterest) : amountToCharge));

  const partialPaymentsEnabled = true;

  useEffect(() => {
    if (isPartialPayment) {
      form.setValue('billingType', BillingType.Receipt);
    }
  }, [isPartialPayment]);

  useEffect(() => {
    if (offering) {
      form.setValue('billingType', offering.isInvoiceable ? BillingType.Invoice : BillingType.Receipt)
    }
  }, [offering])


  const { onSubmit, isPending } = useOnSubmit({ contractId: mainContract.id, paymentId, amountToCharge, customer });

  if (isLoading) {
    return <LoaderElement />
  }

  if (error || !agreementPayments) {
    return <UnexpectedError />
  }

  return (
    <PageContainer className='max-w-4xl'>
      <NavHeader>
        <h1 className="text-2xl font-bold tracking-tight">
          Confirmación del pago y emisión de comprobante
        </h1>
        <p className='text-muted-foreground'>Complete los datos solicitados para confirmar el pago y emitir un comprobante</p>
      </NavHeader>

      <div className="grid grid-cols-1 gap-6 mt-6">
        <div className="">
          <Card className="h-full border-gray-200 shadow-sm flex flex-col overflow-hidden">
            <div className="bg-gray-900 text-white p-6">
              <h2 className="text-lg font-semibold flex items-center justify-between">
                <span>Resumen de Operación</span>
                <span className="text-xs font-mono bg-white/20 px-2 py-1 rounded">{subContract?.contractNumber}</span>
              </h2>
            </div>

            <CardContent className="p-0 flex-1 flex flex-col bg-white">
              <CustomerData customer={customer} />

              <div className="p-6 border-b border-gray-100 flex-1">
                <div className='flex items-center gap-10 mb-5'>
                  <div>
                    <h3 className="text-xs font-bold text-primary mb-3 uppercase tracking-wider">
                      Tipo de servicio a cobrar
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      {offering?.serviceType && <>{serviceTypeBadge(offering.serviceType)}</>}
                    </div>
                  </div>
                  <div className="flex-1">
                    {parcel && (
                      <ParcelContractData parcel={parcel} />
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-[2fr_6fr_3fr] items-center text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2">
                    <span>Rubro</span>
                    <span>Concepto</span>
                    <span>Monto</span>
                  </div>

                  {agreementPayments.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-[2fr_6fr_3fr] items-center">
                      <div className="w-fit">
                        <span className="text-sm font-medium text-gray-900 leading-snug">
                          {item.offeringCode}
                        </span>
                      </div>
                      <div className="pr-4 flex-1">
                        <div className="text-sm font-medium text-gray-900 leading-snug">
                          <div>
                            {item.offeringName} -
                            {item.periodStartDate && item.periodEndDate && (
                              <span className="ml-1 font-semibold font-mono">
                                {getSubscriptionLabel(item.periodStartDate, item.periodEndDate, item.billingFrequency)}
                              </span>
                            )}
                            {amountOwed > 0 && (
                              <span className="ml-1.5 text-xs text-gray-500 font-normal">
                                (${convertToDigitStr(item.contractAmount)})
                              </span>
                            )}
                            {item.paymentPeriodFrom && item.periodStartDate?.getTime() !== item.paymentPeriodFrom.getTime() && (
                              <span className="ml-1 font-semibold">
                                - {renderSinglePeriod(item.paymentPeriodFrom)}
                              </span>
                            )}
                            {item.description && (
                              <span className="ml-1">
                                - {item.description}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        <PriceLabelWithTaxResponsive price={item.totalAmount || 0} taxRate={item.taxRate} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <SubtotalsData amountOwed={amountOwed} amountPayed={0} interestAmount={interestAmount} amountToCharge={amountToCharge} />
            </CardContent>
          </Card>
        </div>

        <Card className="h-full border-gray-200 shadow-sm">
          <CardHeader className="bg-white pb-4 border-b">
            <CardTitle className="text-xl">Acreditación del pago</CardTitle>
            <CardDescription>Complete la información del pago a recibir por parte del cliente</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <PaymentAccreditationForm
              form={form}
              onSubmit={onSubmit}
              isPending={isPending}
              partialPaymentsEnabled={partialPaymentsEnabled}
              maxPartialAmount={totalAmountToBePayed}
              amountToInvoice={amountToInvoice}
              amountToCharge={amountToCharge}
              activeCollectors={collectors}
              assignedCollectorName={assignedCollectorName}
              selectedDifferentCollector={selectedDifferentCollector}
              contractId={subContract?.id}
            />
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
